import { useMemo } from "react";
import { Text, View } from "react-native";

import type { RegressionResult } from "@jackedlogs/shared";

import { LineChart } from "../charts/LineChart";
import { MiniTrendBars } from "../primitives/MiniTrendBars";
import { Chip } from "../primitives/Chip";
import { Icon } from "../Icon/Icon";
import { useDisclosure } from "../primitives/useDisclosure";
import type { UiTheme } from "../theme";
import { makeTierOneStyles } from "./panelStyles";

// Not exported: both panels build these inline in the `chart` prop, so the
// name has no callsite outside this file (knip, 2026-07-25).
type TierOneTrendChartPoint = { x: number; y: number; label: string };

export type TierOneTrendRowProps = {
  theme: UiTheme;
  /**
   * Small uppercase caption above the row. E1RM leads with the movement
   * pattern; an axis with no category (reps-at-load) omits it and lets the
   * value carry the row.
   */
  leadLabel?: string;
  name: string;
  /**
   * measurement-contract §3 "insufficient": no trusted floor / no effort-tagged
   * comparison. Wins over `fewData` — an arrow here would be a confident line
   * through data we can't stand behind, so the honest sentence shows instead.
   */
  insufficient: boolean;
  reg: RegressionResult;
  /**
   * The headline value, pre-formatted by the caller — an e1RM in kg, a
   * "18 × 55 kg" reps-at-load figure. Each axis owns its own units/formatting;
   * this row owns only how the three states LOOK.
   */
  value: string;
  /** §3 honest sentence naming the gesture; carries NO live CTA (fetta 4). */
  notMeasurableText: string;
  fewDataText: string;
  /**
   * What the trend arrow SAYS to a screen reader. The direction lives only in
   * the glyph and its colour — `value` is an absolute, not a signed delta — so
   * without these the number is announced and the direction is not.
   *
   * Per-axis, like `notMeasurableText`: each panel's help text teaches its own
   * legend for the same three arrows ("progressing / plateau / regressing" on
   * e1RM, "climbing / flat / falling" on reps-at-load). A single shared pair of
   * words would tell a screen-reader user a different word than the legend
   * they just read on that very panel.
   */
  trendLabels: { up: string; flat: string; down: string };
  /**
   * EQ-1-HIST slice 2 — which machine produced this row's numbers, and the
   * exercise's other machines behind a tap. Antenore's call (2026-08-19):
   * principal machine on the row, the rest reachable, never two rows and
   * never two lines in one chart.
   *
   * Optional and axis-specific, exactly like `leadLabel`: the reps-at-load
   * axis has no machine concept, passes nothing, and renders as before. So
   * does every untagged exercise — which is all of production today.
   *
   * ⛔ Words and units are the CALLER's, like `notMeasurableText` and
   * `trendLabels`: this row owns only how they look.
   */
  machine?: {
    /** Already localized, e.g. "Machine B". */
    label: string;
    /**
     * The exercise's other machines, best first, each field pre-formatted.
     * EMPTY means there is nothing to open — the label then renders as plain
     * text, never as a control that does nothing when tapped.
     */
    alternates: ReadonlyArray<{
      key: string;
      label: string;
      value: string;
      detail: string;
      /**
       * §3 on this machine's own series (EQ-1-HIST-TRUST): true when its value
       * has no trusted floor behind it, and the row grades it with the same "?"
       * marker the headline shows. Its own bit, never the champion's.
       */
      insufficient: boolean;
      /**
       * What the value announces to a screen reader when `insufficient` — the
       * "?" glyph is decorative/hidden, so without this the untrusted number
       * would be read as a bare figure. Caller-owned words (like every other
       * string here). Absent = the value reads as its own text.
       */
      valueA11yLabel?: string;
    }>;
    /** What the trigger says to a screen reader, e.g. "Machine B, show the
     *  other 2 machines". Only read when there is something to open. */
    a11yLabel: string;
  };
  chart: {
    key: string;
    points: TierOneTrendChartPoint[];
    yFormat: (n: number) => string;
    /**
     * How the series is drawn. The two strength axes are different KINDS of
     * quantity and a shared row must not pretend otherwise (PG-4):
     *
     * - `"line"` (default) — e1RM. A continuous estimate in kg; the line
     *   between two sessions is a legitimate reading of a trend, and the
     *   real spacing between dates carries meaning.
     * - `"bars"` — reps at a fixed load. A COUNT, taken once per session,
     *   usually 3-6 of them. A line there draws a slope through values that
     *   never existed (no one did 13.5 reps between Tuesday and Friday), and
     *   its auto-scaled y turns ±1 rep into a sawtooth. Zero-baseline bars
     *   say the honest thing — this session against that one — and cannot
     *   exaggerate a one-rep difference.
     */
    variant?: "line" | "bars";
  };
};

// One Tier-1 "trend row" in its three measurement-contract §3 states, shared by
// the two strength axes (E1RM + reps-at-load). Extracted at the 2nd callsite
// (§CONSOLIDAMENTO): the icon/colour derivation, the "?"/"·" markers and the
// three-state branch were a ~90-line verbatim copy in each panel and were about
// to drift. Each axis still owns its data, units and i18n — this owns only the
// look of a row, so the two stay in lockstep and a third axis reuses it.
export function TierOneTrendRow({
  theme,
  leadLabel,
  name,
  insufficient,
  reg,
  value,
  notMeasurableText,
  fewDataText,
  trendLabels,
  machine,
  chart,
}: TierOneTrendRowProps) {
  const styles = useMemo(() => makeTierOneStyles(theme), [theme]);
  // The shared fold state machine (CN-disclosure), not a hand-rolled useState:
  // it is what carries `accessibilityState.expanded` to the reader.
  const machines = useDisclosure();
  const hasAlternates = (machine?.alternates.length ?? 0) > 0;
  const fewData = reg.pointCount < 3;
  const trendIcon =
    reg.flag === "progressing"
      ? "trend-up"
      : reg.flag === "regressing"
        ? "trend-down"
        : "arrow-right";
  const trendLabel =
    reg.flag === "progressing"
      ? trendLabels.up
      : reg.flag === "regressing"
        ? trendLabels.down
        : trendLabels.flat;
  // Trend flag = advisory judgement, off the brand (colour budget S2.1):
  // progressing→complete green, regressing→danger red, plateau→neutral ink3.
  const flagColor = fewData
    ? theme.palette.ink3
    : reg.flag === "progressing"
      ? theme.palette.complete
      : reg.flag === "regressing"
        ? theme.palette.danger
        : theme.palette.ink3;
  // The §3 "?" marker — an untrusted value has no floor to headline, so the
  // number is never shown as if it were real. Distinct from fewData's "·" so
  // the two non-measured states read apart at a glance. Decorative and hidden
  // from a11y: the untrusted meaning is spoken in WORDS elsewhere — the headline
  // by `notMeasurableText` below it, an alternate by its value's
  // `valueA11yLabel` — so the "?" is never a glyph-only signal. Shared by the
  // headline AND each untrusted alternate machine (EQ-1-HIST-TRUST) so the one
  // marker can never drift into two.
  const trustFlag = () => (
    <Text
      style={[styles.trendFlag, { color: theme.palette.ink3 }]}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      ?
    </Text>
  );
  return (
    <View style={styles.trendRow}>
      {leadLabel !== undefined ? (
        <Text style={styles.patternLabel}>{leadLabel}</Text>
      ) : null}
      <View style={styles.trendLabelRow}>
        <Text style={styles.balanceLabel} numberOfLines={1}>
          {name}
        </Text>
        {insufficient ? (
          // No trusted value to headline — see trustFlag.
          trustFlag()
        ) : (
          <View style={styles.trendFlagRow}>
            {fewData ? (
              // Same as the "?" above: `fewDataText` below carries the
              // meaning, the dot is decoration.
              <Text
                style={[styles.trendFlag, { color: flagColor }]}
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
              >
                ·
              </Text>
            ) : (
              <Icon
                name={trendIcon}
                size={theme.typography.sizes.sm}
                color={flagColor}
                label={trendLabel}
              />
            )}
            <Text style={[styles.trendFlag, { color: flagColor }]}>{value}</Text>
          </View>
        )}
      </View>
      {machine !== undefined ? (
        <View style={styles.trendMachineRow}>
          {hasAlternates ? (
            // A control only when there IS something behind it. The chip is
            // the shared primitive with `expanded`, added for EQ-1's machine
            // chip precisely so a chip that opens a fold announces
            // collapsed/expanded instead of selected.
            <Chip
              theme={theme}
              // The caret STATES the fold instead of implying it — the rule
              // `GymChip` and `_CurrentExerciseMachineLine` already carry, both
              // of whose comments name "the machine chip" as the reference.
              // This was that chip, and it had shipped without it: `expanded`
              // told a screen reader and NOTHING told a sighted user, who had
              // no signal the label was even tappable.
              label={`${machine.label} ${machines.expanded ? "▴" : "▾"}`}
              // Never `selected`: this chip does not select anything, it opens
              // a fold. `expanded` above is what the reader is told, and the
              // brand fill that `selected` would bring is forbidden here
              // anyway — a machine name is static content, not an action.
              selected={false}
              // ⛔ Not `dense`: it uppercases, and the single-machine case one
              // branch below is plain sentence-case text. The same fact —
              // WHICH machine — would then wear two unrelated typographies
              // depending on whether the athlete happens to own a second one.
              // Both siblings above skip `dense` for the same chip, too.
              prominent
              expanded={machines.expanded}
              onPress={machines.toggle}
              accessibilityLabel={machine.a11yLabel}
            />
          ) : (
            // One machine and nothing to open: it is a fact about the row,
            // not an affordance. Rendering it as a chip would promise a tap
            // that does nothing.
            <Text style={styles.trendMachineStatic}>{machine.label}</Text>
          )}
        </View>
      ) : null}
      {machine !== undefined && machines.expanded ? (
        <View style={styles.trendAlternates}>
          {machine.alternates.map((a) => (
            <View key={a.key} style={styles.trendAlternateRow}>
              <Text style={styles.trendAlternateName} numberOfLines={1}>
                {a.label}
              </Text>
              <View style={styles.trendFlagRow}>
                {a.insufficient ? trustFlag() : null}
                <Text
                  style={styles.trendAlternateValue}
                  accessibilityLabel={a.valueA11yLabel}
                >
                  {a.value}
                </Text>
                <Text style={styles.trendAlternateDetail}>{a.detail}</Text>
              </View>
            </View>
          ))}
        </View>
      ) : null}
      {insufficient ? (
        <Text style={styles.notMeasurable}>{notMeasurableText}</Text>
      ) : fewData ? (
        <Text style={styles.fewDataBadge}>{fewDataText}</Text>
      ) : (
        chart.variant === "bars" ? (
          // Reuses the existing bars primitive rather than growing a second
          // one: `weeks` is "ordered buckets, index IS time", which is exactly
          // what a per-session series is once the funnel emits one point per
          // day (PG-4). The last bar is emphasised by the primitive's own
          // idiom, which lands on the most recent session — the one the
          // headline value quotes.
          <MiniTrendBars
            theme={theme}
            weeks={chart.points.map((p) => p.y)}
            height={48}
            barWidth={10}
            gap={4}
          />
        ) : (
          <LineChart
            series={[{ key: chart.key, variant: "focal", points: chart.points }]}
            yFormat={chart.yFormat}
            showMeta={false}
            height={48}
            theme={theme}
          />
        )
      )}
    </View>
  );
}
